import { History } from 'history';
import { Action } from 'redux';

import { RouteLocationState } from '../common/WithQueryParams';
import {
  AxiosAction,
  isSuccessActionOf
} from '../middleware/axios';
import { isAction } from '../utils';

import {
  CheckpointDto,
  CheckpointResponse
} from './state';

export interface GetCheckpointsParams {
  filter?: string;
  locationCode?: string | null;
  type?: string;
  skip?: number;
  take?: number;
}

// Action Types

const GET_SINGLE_CHECKPOINT = 'CHECKPOINTS.GET';
const GET_CHECKPOINTS = 'CHECKPOINTS.GET_ALL';
const GET_CHECKPOINT_COUNT = 'CHECKPOINTS.COUNT';
const CLEAR_RECENTLY_MODIFIED = 'CHECKPOINTS.CLEAR_RECENTLY_MODIFIED';
const DELETE_CHECKPOINT = 'CHECKPOINT.DELETE';
const CREATE_CHECKPOINT = 'CHECKPOINTS.CREATE';
const UPDATE_CHECKPOINT = 'CHECKPOINTS.UPDATE';
const ASSIGN_CHECKPOINT_LOCATION = 'CHECKPOINTS.ASSIGN_CHECKPOINT_LOCATION';

// Action Interfaces

export interface UpdateCheckpoint extends AxiosAction<CheckpointResponse> {
  type: typeof UPDATE_CHECKPOINT;
}
export interface CreateCheckpoint extends AxiosAction<CheckpointResponse> {
  type: typeof CREATE_CHECKPOINT;
}
export interface GetCheckpoints extends AxiosAction<CheckpointResponse[]> {
  type: typeof GET_CHECKPOINTS;
}
export interface GetSingleCheckpoint extends AxiosAction<CheckpointResponse> {
  type: typeof GET_SINGLE_CHECKPOINT;
}
export interface GetCheckpointCount extends AxiosAction<number> {
  type: typeof GET_CHECKPOINT_COUNT;
}
export interface DeleteCheckpoint extends AxiosAction<void> {
  code: string;
  type: typeof DELETE_CHECKPOINT;
}
export interface AssignCheckpointLocation extends AxiosAction<void> {
  type: typeof ASSIGN_CHECKPOINT_LOCATION;
}
export interface ClearRecentlyModified extends Action {
  type: typeof CLEAR_RECENTLY_MODIFIED;
}

// Action Creators

export function getCheckpoints(params: GetCheckpointsParams): GetCheckpoints {
  return {
    payload: {
      client: 'workManagement',
      request: {
        params,
        url: '/checkpoints'
      }
    },
    type: GET_CHECKPOINTS
  };
}
export const isGetCheckpoints = isAction<GetCheckpoints>(GET_CHECKPOINTS);
export const isGetCheckpointsSuccess = isSuccessActionOf<GetCheckpoints>(GET_CHECKPOINTS);

export function getCheckpoint(code: string): GetSingleCheckpoint {
  return {
    payload: {
      client: 'workManagement',
      request: {
        url: `/checkpoints/${code}`
      }
    },
    type: GET_SINGLE_CHECKPOINT
  };
}
export const isGetSingleCheckpoint = isAction<GetSingleCheckpoint>(GET_SINGLE_CHECKPOINT);
export const isGetSingleCheckpointSuccess = isSuccessActionOf<GetSingleCheckpoint>(GET_SINGLE_CHECKPOINT);

export function getCheckpointCount(params: GetCheckpointsParams): GetCheckpointCount {
  return {
    payload: {
      client: 'workManagement',
      request: {
        params,
        url: '/checkpoints/count'
      }
    },
    type: GET_CHECKPOINT_COUNT
  };
}
export const isGetCheckpointCount = isAction<GetCheckpointCount>(GET_CHECKPOINT_COUNT);
export const isGetCheckpointCountSuccess = isSuccessActionOf<GetCheckpointCount>(GET_CHECKPOINT_COUNT);

export function deleteCheckpoint(
  code: string,
  onSuccess?: DeleteCheckpoint['onSuccess']
): DeleteCheckpoint {
  return {
    code,
    onSuccess,
    payload: {
      client: 'workManagement',
      request: {
        method: 'DELETE',
        url: `/checkpoints/${code}`
      }
    },
    type: DELETE_CHECKPOINT
  };
}
export const isDeleteCheckpoint = isAction<DeleteCheckpoint>(DELETE_CHECKPOINT);
export const isDeleteCheckpointSuccess = isSuccessActionOf<DeleteCheckpoint>(DELETE_CHECKPOINT);

function getPreviousUrl(history: History): string {
  const previousState = history.location.state as RouteLocationState;
  return previousState ?
    previousState.previousLocation.pathname + previousState.previousLocation.search : '/checkpoints';
}

export function createCheckpoint(data: CheckpointDto, history?: History): CreateCheckpoint {
  return {
    onSuccess: () => dispatch => {
      if (history) {
        dispatch(() => history.push(getPreviousUrl(history)));
      }
    },
    payload: {
      client: 'workManagement',
      request: {
        data,
        method: 'POST',
        url: '/checkpoints'
      }
    },
    type: CREATE_CHECKPOINT
  };
}
export const isCreateCheckpoint = isAction<CreateCheckpoint>(CREATE_CHECKPOINT);
export const isCreateCheckpointSuccess = isSuccessActionOf<CreateCheckpoint>(CREATE_CHECKPOINT);

export function updateCheckpoint(code: string, data: CheckpointDto, history: History): UpdateCheckpoint {
  return {
    onSuccess: () => dispatch => dispatch(() => history.push(getPreviousUrl(history))),
    payload: {
      client: 'workManagement',
      request: {
        data,
        method: 'PUT',
        url: `/checkpoints/${code}`
      }
    },
    type: UPDATE_CHECKPOINT
  };
}
export const isUpdateCheckpoint = isAction<UpdateCheckpoint>(UPDATE_CHECKPOINT);
export const isUpdateCheckpointSuccess = isSuccessActionOf<UpdateCheckpoint>(UPDATE_CHECKPOINT);

export function assignCheckpointLocation(
  locationCode: string | null,
  checkpointCodes: string[]): AssignCheckpointLocation {

  return {
    payload: {
      client: 'workManagement',
      request: {
        data: { locationCode, checkpointCodes },
        method: 'POST',
        url: `/checkpoints/assign-location`
      }
    },
    type: ASSIGN_CHECKPOINT_LOCATION
  };
}
export const isAssignCheckpointLocation = isAction<AssignCheckpointLocation>(ASSIGN_CHECKPOINT_LOCATION);
export const isAssignCheckpointLocationSuccess =
  isSuccessActionOf<AssignCheckpointLocation>(ASSIGN_CHECKPOINT_LOCATION);

export function clearRecentlyModified() {
  return {
    type: CLEAR_RECENTLY_MODIFIED
  };
}
export const isClearRecentlyModified = isAction<ClearRecentlyModified>(CLEAR_RECENTLY_MODIFIED);
